import { useEffect, useRef, useState } from 'react';

import { httpStatusOf, messageOf } from '@/admin/users/adminErrors';

/**
 * Lecture d'une route d'administration, en etat deduit.
 *
 * Ce qu'il fait de plus qu'un `useEffect` ecrit a la main, trois fois dans l'ecran :
 *
 * - **L'attente est deduite, jamais ecrite** (meme principe que `useAdminAccess`) :
 *   tant que l'issue rangee ne porte pas la cle et le jeton de la demande en
 *   cours, on attend. Aucun `setState` pose dans l'effet.
 * - **Une donnee ne survit pas a un changement de cle.** Ouvrir une autre
 *   collection ne laisse pas l'ancienne sous le nouveau titre. En revanche une
 *   **relecture** (meme cle, nouveau jeton) garde la derniere donnee affichee :
 *   un echec d'actualisation n'efface pas ce que l'exploitant lisait.
 * - **Le jeton vient du parent.** « Actualiser », « Reessayer » et le retour sur
 *   l'onglet relisent d'un meme geste tout ce que l'ecran montre ; aucune
 *   minuterie (ADR 0035 §4 : chaque lecture ecrit une ligne d'audit).
 */

/** Ce que l'ecran doit distinguer d'un echec : trois statuts portent un sens propre. */
export interface QueryFailure {
  readonly message: string;
  /** 403 : il manque un role. Ni une panne, ni une deconnexion. */
  readonly forbidden: boolean;
  /** 410 `account_anonymized` : plus aucun compte actif n'est rattache au perimetre. */
  readonly anonymized: boolean;
  /** 404 : rien ne correspond. */
  readonly notFound: boolean;
  /** 422 : le serveur a refuse la demande (filtre illisible, intervalle inverse). Relancer ne la corrige pas. */
  readonly invalid: boolean;
}

export interface QueryState<T> {
  readonly data: T | null;
  readonly failure: QueryFailure | null;
  /** Vrai tant que la demande courante n'a pas abouti, relecture comprise. */
  readonly busy: boolean;
}

interface Outcome<T> {
  readonly key: string;
  readonly token: number;
  readonly data: T | null;
  readonly failure: QueryFailure | null;
}

export const failureOf = (error: unknown): QueryFailure => {
  const status = httpStatusOf(error);

  return {
    message: messageOf(error),
    forbidden: status === 403,
    anonymized: status === 410,
    notFound: status === 404,
    invalid: status === 422,
  };
};

export const useAdminQuery = <T,>(
  key: string,
  load: () => Promise<T>,
  { enabled = true, token = 0 }: { readonly enabled?: boolean; readonly token?: number } = {},
): QueryState<T> => {
  const [outcome, setOutcome] = useState<Outcome<T> | null>(null);
  const loader = useRef(load);

  // Garde la derniere fermeture sans en faire une dependance de la lecture : une
  // fonction ecrite en ligne change a chaque rendu et relancerait la requete.
  useEffect(() => {
    loader.current = load;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        const data = await loader.current();

        if (!cancelled) {
          setOutcome({ key, token, data, failure: null });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setOutcome((previous) => ({
            key,
            token,
            // Meme cle : la derniere donnee reste affichee sous l'erreur.
            data: previous?.key === key ? previous.data : null,
            failure: failureOf(error),
          }));
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [key, token, enabled]);

  const sameKey = outcome?.key === key;
  const settled = sameKey && outcome.token === token;

  return {
    data: sameKey ? outcome.data : null,
    failure: settled ? outcome.failure : null,
    busy: enabled && !settled,
  };
};

/**
 * Relit au retour sur l'onglet (ADR 0035 §4) : l'evenement `visibilitychange`
 * passant a « visible » incremente le jeton. Aucune relecture sur minuterie.
 * `onReturn` doit etre stable (`useCallback`).
 */
export const useRefreshOnReturn = (onReturn: () => void): void => {
  useEffect(() => {
    const listener = (): void => {
      if (document.visibilityState === 'visible') {
        onReturn();
      }
    };

    document.addEventListener('visibilitychange', listener);

    return () => {
      document.removeEventListener('visibilitychange', listener);
    };
  }, [onReturn]);
};
